import * as Sentry from "@sentry/nextjs"
import { Button, Modal, Spinner } from "flowbite-react"
import { FormikProps } from "formik"
import { useRouter } from "next/router"
import React, { useRef, useState } from "react"

import { CloseIcon, HomeIcon, LeftSmallIcon, RightSmallIcon } from "@assets/icons"
import CustomImage from "@components/elements/CustomImage"
import Steps from "@components/elements/FormStepper/stepper"
import useAuth from "@hooks/useAuth"
import BookingSwLogo from "@icons/BookingSwLogo"
import { PerOrderFieldsFormAttributes } from "@modules/tenants/OrdersPage/OrderSummaryPage/PerOrderFieldsModal"
import { domainPlaceholder, widgetsDomainName } from "@mono/essentials/constants/options"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import { CreateCartRequestType } from "@mono/essentials/types/CartTypes"
import { GiftCardBuySteps } from "@mono/essentials/utils/Constant"

import CustomerDetailsForm from "../CustomerDetailsForm"
import CustomerLogin from "../CustomerLogin"

import GiftCardViewDetails from "./GiftCardViewDetails"

const ProductGiftCardBuyModal = ({
  domainDetails,
  closeModal,
  goToBackStep,
  show,
  selectedProduct,
  selectedProductData,
  isSelectedProductDetailValidating,
  isIntegratedPage,
  onSuccess,
  cartId,
  updateCart,
  cartItemDetails,
  cartParticipantsDetails,
  isFromCheckOutModal,
  customer_id,
  productInitialValues,
  isCustomWidget,
  customerMembershipOptions,
  membershipId,
}: any) => {
  const [currentStep, setCurrentStep] = useState(
    GiftCardBuySteps.findIndex((item: any) => item.value === "viewDetails")
  )
  const noOfActiveProductInCategory = selectedProductData?.categories[0]?.active_products_counts

  const [productInfo, setProductInfo] = useState(undefined)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [giftCardParticipantItems, setGiftCardParticipantItems] = useState(undefined)
  const customerDetailsRef = useRef<FormikProps<PerOrderFieldsFormAttributes>>(null)
  const router = useRouter()
  const { loggedInCustomerDetails } = useAuth()
  const { affiliateID } = router.query as any
  const handleResetData = (isCancel = false) => {
    setCurrentStep(0)
    setProductInfo(undefined)
    setGiftCardParticipantItems(undefined)
    closeModal(isCancel)
  }

  const goToNextStep = () => {
    setCurrentStep((prev) => prev + 1)
  }

  const handleCreateCart = async (
    participantData?: any,
    customerId?: string,
    customerMembershipId?: string
  ) => {
    try {
      setIsSubmitting(true)
      const selectedCustomerId = loggedInCustomerDetails?.id || customerId || customer_id
      const _membershipId = customerMembershipId || membershipId
      const participantsData = participantData || giftCardParticipantItems
      const quantitesData = participantsData?.participantsDetails?.map((item) => item.amount)
      const createCartPayload: CreateCartRequestType = {
        affiliate_id: affiliateID,
        customer_id: selectedCustomerId,
        items: [
          {
            product_id: selectedProduct,
            quantities: quantitesData?.map((amount) => ({
              quantity: 1,
              amount,
            })),
          },
        ],
      }

      if (cartId && cartItemDetails && updateCart)
        createCartPayload.items.unshift(
          ...(cartItemDetails.items || []).map((item) => ({
            ...item,
            product_id: item.product_id,
            slot_id: item.slot_id,
            quantities: item.quantities,
          }))
        )

      const { data: createCartResponse } =
        updateCart && cartId
          ? await OrderServices.updateCart(cartId, createCartPayload, isIntegratedPage)
          : await OrderServices.createCart(createCartPayload, isIntegratedPage)
      if (createCartResponse.cart.id && _membershipId) {
        const payload = {
          code_type: "membership_code",
          code: _membershipId,
        }
        await OrderServices.applyMembershipToCart(
          createCartResponse.cart.id,
          payload,
          isIntegratedPage
        )
      }
      if (
        createCartResponse.cart.id &&
        participantsData &&
        participantsData.participantsDetails?.length
      ) {
        const payload: {
          value: string | number | string[] | boolean
          product_field_id: string
          cart_item_id: string
        }[] = []
        participantsData.participantsDetails.forEach(
          (perOrderFieldDetails: any, _index: string) => {
            Object.keys(perOrderFieldDetails)
              .filter((fieldName) => !(fieldName.includes("_id") || fieldName === "amount"))
              .forEach((fieldName) => {
                payload.push({
                  value: perOrderFieldDetails[fieldName],
                  product_field_id: perOrderFieldDetails[`${fieldName}_id`] as unknown as string,
                  cart_item_id: createCartResponse?.cart?.items[0]?.id,
                })
              })
          }
        )
        OrderServices.addGiftCardDetailsToCartItem(
          createCartResponse.cart.id,
          payload,
          isIntegratedPage
        )
      }
      onSuccess?.(createCartResponse.cart.id)
      setIsSubmitting(false)
      // resetCheckOutForm()
      handleResetData()
    } catch (e) {
      Sentry.captureException(e)
      console.error({ e })
    } finally {
      setIsSubmitting(false)
    }
  }

  const getModalContent = (steps: number) => {
    switch (steps) {
      case 0:
        return (
          <GiftCardViewDetails
            handleNextStep={(giftCardItemsData, quantityCount) => {
              setGiftCardParticipantItems(giftCardItemsData)
              if (isFromCheckOutModal) {
                handleCreateCart(giftCardItemsData)
              } else {
                goToNextStep()
              }
            }}
            formInitialValues={giftCardParticipantItems}
            selectedProduct={selectedProduct}
            isIntegratedPage={isIntegratedPage}
            setProductInfo={setProductInfo}
            domainDetails={domainDetails}
            selectedProductData={selectedProductData}
            isSelectedProductDetailValidating={isSelectedProductDetailValidating}
          />
        )
      case 1:
        return (
          <>
            <div className={"rounded-md"}>
              <CustomerDetailsForm
                onSuccess={(customer_id, perOrderFieldsValues, customerMembershipId) => {
                  handleCreateCart(undefined, customer_id, customerMembershipId)
                }}
                customerDetailsRef={customerDetailsRef}
                isIntegratedPage={isIntegratedPage}
                selectedProductId={selectedProduct}
                customerMembershipOptions={customerMembershipOptions}
                membershipId={membershipId}
                domainDetails={domainDetails}
              />
            </div>
            <Button
              size={"md"}
              className={"float-right mt-4 [&>span]:!text16Medium [&>span]:!py-[13px] !h-10"}
              type={"submit"}
              color={"primary"}
              onClick={() => customerDetailsRef.current?.submitForm()}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner size={"md"} light /> : "Next"}
              <RightSmallIcon width={20} height={20} className={"ml-2"} />
            </Button>
          </>
        )
      default:
        return null
    }
  }

  const handleBackButton = () => {
    // Handle non-first steps
    if (currentStep !== 0) {
      return setCurrentStep((prev) => prev - 1)
    }

    // Handle single product in category
    if (noOfActiveProductInCategory === 1) {
      if (isCustomWidget) {
        return handleResetData(true)
      }
      setProductInfo(undefined)
      setGiftCardParticipantItems(undefined)
      goToBackStep(true)
    } else if (currentStep == 0) {
      if (isCustomWidget) {
        handleResetData(true)
      } else {
        goToBackStep()
        handleResetData()
      }
    } else {
      setCurrentStep((prev) => prev - 1)
    }
  }

  return (
    <Modal
      show={show}
      onClose={() => {
        console.log("onModalClose")
        handleResetData(true)
      }}
      size={"6xl"}
      className={"widget-theme-wrapper"}
      theme={{
        content: {
          inner: `relative rounded-lg bg-transparent shadow dark:bg-gray-700 flex flex-col max-h-[92vh]`,
          base: `relative w-full h-auto rounded-lg max-h-[92vh] overflow-hidden mx-2 !max-w-[1180px]`,
        },
      }}
    >
      <Modal.Body className={"!bg-transparent rounded-lg"}>
        {/* Stepper */}
        <div className={"flex items-center px-4 mb-2 bg-white rounded-lg sm:px-8"}>
          <div className={"border-r border-neutral-300"} onClick={() => handleResetData(true)}>
            <HomeIcon className={"w-4 h-4 mr-2 cursor-pointer text-primary-600"} />
          </div>
          <div
            className={
              "flex items-center justify-center border-r cursor-pointer border-neutral-300 sm:pr-6"
            }
            onClick={handleBackButton}
          >
            <LeftSmallIcon width={18} height={18} className={"mx-2 text-primary-600"} />
            <div className={"hidden text14Medium text-primary-600 sm:block"}>Back</div>
          </div>
          <Steps
            stepWrapperClassName={"w-full "}
            wrapperClassName={"w-full shadow-none"}
            steps={GiftCardBuySteps}
            currentStep={currentStep}
            innerClassName={"flex"}
            isWidget
          />
        </div>

        {/* Content */}
        <div className={"rounded-t-lg"}>
          <div className={"px-6 py-4 bg-white border-b rounded-t-lg border-neutral-200"}>
            <div className={"sm:flex block sm:items-center items-start justify-between"}>
              <div className={"flex sm:items-center items-start"}>
                {domainDetails?.logo ? (
                  <div className={"mr-2 pr-2 border-r w-fit border-neutral-300"}>
                    {domainDetails?.logo !== "" ? (
                      <CustomImage
                        src={domainDetails?.logo}
                        // className={'overflow-hidden rounded-lg mr-2.5'}
                        className={"w-auto max-h-9"}
                        height={40}
                        width={80}
                        fill={false}
                      />
                    ) : (
                      <BookingSwLogo className={"mr-2"} />
                    )}
                  </div>
                ) : null}
                <div>
                  <div className={"flex flex-row items-center gap-2"}>
                    <span className={"lg:text18Medium text14Medium lg:w-auto text-neutral-900"}>
                      {selectedProductData?.name}
                    </span>
                  </div>
                </div>
              </div>
              <div className={"cart-logo-container"}>
                <div
                  className={
                    "flex items-center sm:justify-normal justify-between gap-4 whitespace-nowrap"
                  }
                >
                  <div
                    className={
                      "sm:flex inline-flex sm:flex-nowrap flex-wrap gap-1 mt-1.5 sm:mt-0 sm:gap-0"
                    }
                  >
                    {isIntegratedPage ? (
                      <CustomerLogin isIntegratedPage={isIntegratedPage} />
                    ) : null}
                  </div>
                  <div onClick={() => handleResetData(true)}>
                    <CloseIcon className={"h-4.5 w-4.5 cursor-pointer"} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={"bg-whiteBlack-100 sm:p-6 p-2 overflow-auto max-h-[calc(92vh-215px)]"}>
            {getModalContent(currentStep)}
          </div>
        </div>
        <div
          className={`flex flex-1 flex-row items-center justify-between border-t border-neutral-200 bg-whiteBlack-100 sm:px-6 sm:py-4 px-4 py-3`}
        >
          <div className={"flex items-center"}>
            <div className={"flex flex-col"}>
              <div className={"paragraph13 text-neutral-1000"}>
                Powered by
                <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
                  <span className={"text-primary-600 font-medium"}>{`${widgetsDomainName}`}</span>
                </a>
              </div>
              {domainDetails && domainDetails?.currency_iso3 ? (
                <div
                  className={"text12Medium text-neutral-600"}
                >{`All prices are in ${domainDetails?.currency_iso3}`}</div>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className={"mr-2 w-fit"}>
            <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
              <BookingSwLogo />
            </a>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default ProductGiftCardBuyModal
